import Button from '@atoms/Button/Button';
import ConditionalWrapper from '@atoms/ConditionalWrapper/ConditionalWrapper';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { IImage } from '@models/IImage';
import { IProductCard } from '@models/IProductCard';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import ProductCardVariantEnum from '@models/enums/ProductCardVariantEnum';
import SiteEnum from '@models/enums/SiteEnum';
import ProductCardImage from '@molecules/ProductCardImage/ProductCardImage';
import ProductImageGallery from '@molecules/ProductImageGallery/ProductImageGallery';
import { WishlistButtonContext } from '@utility/context/WishlistButtonContext';
import useIsTouchDevice from '@utility/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { motion } from 'motion/react';
import { useContext, useState } from 'react';

export interface IProductCardProps extends IProductCard {
  readonly site?: SiteEnum;
  readonly showLikeButton?: boolean;
  readonly id?: string;
  readonly wishlistId?: number;
}

const ProductCard = (props: IProductCardProps) => {
  const {
    category,
    cta,
    detailedView = true,
    id,
    images,
    productImage,
    productName,
    productRole,
    showLikeButton = true,
    site,
    tags,
    usps,
    variant = ProductCardVariantEnum.Default,
    wishlistDrawerLabels,
    wishlistId,
  } = props;

  const WishlistButton = useContext(WishlistButtonContext);
  const [isHovered, setIsHovered] = useState(false);
  const isTouchDevice = useIsTouchDevice();

  const fadeUpVariants = {
    hide: {
      y: isTouchDevice ? 0 : '100%',
      opacity: isTouchDevice ? 1 : 0,
    },
    show: { y: 0, opacity: 1 },
  };

  const fadeInVariants = {
    hide: { opacity: isTouchDevice ? 1 : 0 },
    show: { opacity: 1 },
  };

  const handleTrackingEvent = () => {
    window.dataLayer.push({
      event: 'trackingEvent_select_item',
      event_name_variable: 'select_item',
      trackingEvent_select_item: {
        item_name: productName,
        item_category: 'product',
        item_category2: category,
      },
    });
  };

  const imageAspectRatio = (() => {
    switch (variant) {
      case ProductCardVariantEnum.Article:
        return 'tw-w-[110px] md:tw-w-[191px] tw-aspect-[6/7] tw-shrink-0';
      case ProductCardVariantEnum.Tall:
        return 'tw-aspect-[2/3]';
      case ProductCardVariantEnum.TallOnDesktopOnly:
        return 'tw-aspect-[6/7] md:tw-aspect-[2/3]';
      default:
        return 'tw-aspect-[6/7]';
    }
  })();

  return (
    <div className="tw-relative tw-h-full">
      {showLikeButton && WishlistButton && (
        <WishlistButton
          wishlistId={wishlistId}
          card={props}
          cardId={site === SiteEnum.B2B && id ? id : `${cta.url}.html`} // To match existing format
          collection={CardTypeEnum.Products}
          wishlistDrawerLabels={wishlistDrawerLabels}
        />
      )}

      <ConditionalWrapper
        condition={isTouchDevice || !detailedView}
        wrapper={(children) => (
          <a
            className="focus-outline tw-block tw-h-full"
            href={cta.url}
            onClick={handleTrackingEvent}
          >
            {children}
          </a>
        )}
      >
        <article
          className={classNames(
            'focus-outline tw-group tw-relative tw-flex tw-w-full tw-overflow-hidden ',
            variant === ProductCardVariantEnum.Article
              ? 'tw-min-h-[130px] tw-flex-row tw-bg-grey-10 focus:tw-bg-grey-10 md:tw-min-h-[222px]'
              : 'tw-h-full tw-flex-col tw-bg-white focus:tw-bg-white'
          )}
          onPointerEnter={() => setIsHovered(true)}
          onPointerLeave={() => setIsHovered(false)}
          onFocus={() => setIsHovered(true)}
          onBlur={() => setIsHovered(false)}
        >
          <div
            className={classNames(
              'tw-relative tw-shrink-0 tw-overflow-hidden tw-bg-grey-20',
              imageAspectRatio
            )}
          >
            <motion.div
              initial="show"
              animate={isHovered && !!images?.length ? 'hide' : 'show'}
              exit="show"
              variants={fadeInVariants}
              transition={{ ease: 'easeInOut', duration: 0.5 }}
              className="tw-will-change-opacity tw-h-full tw-w-full"
            >
              <ProductCardImage
                productImage={productImage.image}
                tags={variant === ProductCardVariantEnum.Article ? [] : tags}
              />
            </motion.div>

            {!!images?.length && (
              <motion.div
                initial="hide"
                animate={isHovered ? 'show' : 'hide'}
                exit="hide"
                variants={fadeInVariants}
                transition={{ ease: 'easeInOut', duration: 0.5 }}
                className="tw-will-change-opacity tw-absolute tw-top-0 tw-z-10 tw-h-full tw-w-full"
              >
                {!isTouchDevice && images.length === 1 && images[0] ? (
                  <Image
                    image={images[0] as IImage}
                    imageClasses="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-h-full tw-object-center tw-object-cover"
                  />
                ) : (
                  <ProductImageGallery
                    images={images}
                    initialIndex={0}
                    showProductImage={isTouchDevice}
                    mobileFirstImage={productImage.image}
                    tags={
                      variant === ProductCardVariantEnum.Article ? [] : tags
                    }
                    imageAspectRatio={imageAspectRatio}
                  />
                )}
              </motion.div>
            )}
          </div>
          <div
            className={classNames(
              'tw-relative tw-flex tw-grow tw-flex-col tw-text-grey-100',
              variant === ProductCardVariantEnum.TallOnDesktopOnly &&
                'max-md:tw-pb-5',
              variant === ProductCardVariantEnum.Article
                ? 'tw-m-md tw-justify-center'
                : 'tw-m-xs '
            )}
          >
            <Text variant="label-12" className="tw-uppercase tw-text-grey-70">
              {category}
            </Text>
            <Text variant="heading-21" as="p" className="tw-font-light">
              {productName}
            </Text>
            {(detailedView || variant === ProductCardVariantEnum.Article) && (
              <Text variant="body-small" className="tw-mt-xs tw-text-grey-70">
                {productRole}
              </Text>
            )}
            {detailedView && (
              <>
                <motion.ul
                  initial="show"
                  animate={isHovered ? 'hide' : 'show'}
                  exit="show"
                  variants={fadeInVariants}
                  transition={{ ease: 'easeInOut', duration: 0.3 }}
                  className="tw-will-change-opacity tw-mt-xs tw-min-h-xl tw-list-disc tw-px-xs tw-text-xs tw-text-grey-70"
                >
                  {usps.map((usp) => (
                    <li key={usp}>
                      <Text variant="body-small">{usp}</Text>
                    </li>
                  ))}
                </motion.ul>
                {!isTouchDevice && (
                  <motion.div
                    initial="hide"
                    animate={isHovered ? 'show' : 'hide'}
                    exit="hide"
                    variants={fadeUpVariants}
                    transition={{ ease: [0.74, 0, 0.19, 1.02], duration: 0.5 }}
                    className={classNames(
                      isTouchDevice ? 'tw-hidden' : 'tw-block',
                      'tw-w-full tw-will-change-transform md:tw-absolute md:tw-bottom-0 md:tw-left-0'
                    )}
                  >
                    <Button
                      as="link"
                      variant="grey80"
                      label={cta.text}
                      href={cta.url}
                      onClick={handleTrackingEvent}
                    />
                  </motion.div>
                )}
              </>
            )}
          </div>
        </article>
      </ConditionalWrapper>
    </div>
  );
};

export default ProductCard;
